(() => {
  if (customElements.get('maison-header-search')) return;
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().trim();
  const words = value => normalize(value).match(/[\p{L}\p{N}]+/gu) || [];
  function distance(a, b) {
    const rows = Array.from({length:a.length + 1}, () => Array(b.length + 1).fill(0));
    for (let i=0;i<=a.length;i++) rows[i][0]=i;
    for (let j=0;j<=b.length;j++) rows[0][j]=j;
    for (let i=1;i<=a.length;i++) for (let j=1;j<=b.length;j++) {
      rows[i][j]=Math.min(rows[i-1][j]+1,rows[i][j-1]+1,rows[i-1][j-1]+Number(a[i-1]!==b[j-1]));
      if(i>1&&j>1&&a[i-1]===b[j-2]&&a[i-2]===b[j-1]) rows[i][j]=Math.min(rows[i][j],rows[i-2][j-2]+1);
    }
    return rows[a.length][b.length];
  }
  function matchTitle(title, query) {
    const parts=words(query), tokens=words(title), text=normalize(title), corrected=[];
    if(!parts.length)return null;
    let score=0;
    for(const part of parts) {
      if(text.includes(part)){corrected.push(part);continue;}
      if(part.length<4||part.length>40)return null;
      const limit=part.length>=8?2:1;
      let best=null;
      for(const token of tokens) {
        if(Math.abs(token.length-part.length)>limit)continue;
        const edits=distance(part,token);
        if(edits<=limit&&(!best||edits<best.edits))best={token,edits};
      }
      if(!best)return null;
      corrected.push(best.token);score+=best.edits;
    }
    return {score,query:corrected.join(' ')};
  }
  const identity = item => {
    const url=new URL(item.url,location.origin), parts=url.pathname.split('/').filter(Boolean);
    const type=parts.lastIndexOf(item.kind==='collection'?'collections':'products');
    return type>=0?parts.slice(type,type+2).join('/'):url.pathname.replace(/\/$/,'');
  };
  function unique(items) {
    const ids=new Set(), paths=new Set();
    return items.filter(item=>{
      if(!item.title||!safeURL(item.url))return false;
      const id=item.id?String(item.id):null, key=identity(item);
      if((id&&ids.has(id))||paths.has(key))return false;
      if(id)ids.add(id);paths.add(key);return true;
    });
  }
  const safeURL = value => { try { const url = new URL(value, location.origin); return /^https?:$/.test(url.protocol) ? url.href : null; } catch { return null; } };
  const parseData = html => {
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-tiffany-search-data]');
    if (!node) throw new Error('Search data unavailable');
    return JSON.parse(node.textContent);
  };
  const indexes = new Map();
  function catalog(endpoint) {
    if (indexes.has(endpoint)) return indexes.get(endpoint);
    const promise = (async () => {
      const result = { products: [], collections: [] }, visited = new Set();
      let url = new URL(endpoint, location.origin);
      while (url) {
        url.searchParams.set('section_id', 'tiffany-search-index');
        if (visited.has(url.href)) break;
        visited.add(url.href);
        const response = await fetch(url, { signal: AbortSignal.timeout(8000) });
        if (!response.ok) throw new Error('Catalog unavailable');
        const data = parseData(await response.text());
        result.products.push(...(data.products || []));
        result.collections.push(...(data.collections || []));
        url = data.next ? new URL(data.next, url) : null;
        if (url && url.origin !== location.origin) throw new Error('Invalid catalog page');
      }
      return result;
    })();
    indexes.set(endpoint, promise);
    promise.catch(() => indexes.delete(endpoint));
    return promise;
  }
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  function categoryIcon(title) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 40 40'); svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.2'); svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', /muebl|furn|mobil|мебел/.test(normalize(title)) ? 'M11 24V12q0-8 9-8t9 8v12M9 18H6v11h28V18h-3M11 29l-2 7m20-7 2 7M10 24h20M15 9h10' : 'M13 5h14l-2 5v5c0 7 6 7 6 12 0 4-5 6-11 6S9 31 9 27c0-5 6-5 6-12v-5l-2-5ZM14 10h12M15 15h10M15 33v3h10v-3M13 14C3 7 3 25 12 23m15-9c10-7 10 11 1 9');
    svg.append(path); return svg;
  }
  class MaisonHeaderSearch extends HTMLElement {
    connectedCallback() {
      this.controller?.abort(); this.controller = new AbortController();
      this.input = this.querySelector('input[type=search]'); this.popup = this.querySelector('.maison-header-search__popup'); this.results = this.querySelector('.maison-header-search__results'); this.form = this.querySelector('form'); this.status = this.querySelector('[data-maison-search-status]'); this.closeButton = this.querySelector('[data-search-close]'); this.clearButton = this.querySelector('[data-search-clear]');
      if (!this.input || !this.popup) return;
      this.sequence = 0; this.activeIndex = -1; this.options = [];
      const {signal} = this.controller;
      this.clearButton?.addEventListener('click', () => this.clear(), {signal});
      this.querySelectorAll('[data-search-close], [data-search-dismiss]').forEach(button => button.addEventListener('click', () => this.exit(), {signal}));
      this.input.addEventListener('input', event => { if (!event.isComposing) this.schedule(); }, {signal});
      this.input.addEventListener('compositionend', () => this.schedule(), {signal});
      this.input.addEventListener('search', () => this.schedule(), {signal});
      this.input.addEventListener('keydown', event => this.keydown(event), {signal});
      this.form.addEventListener('submit', event => { if (!this.input.value.trim()) { event.preventDefault(); this.input.focus(); } else if(this.correction && normalize(this.input.value)===normalize(this.query)) { this.input.value=this.correction; } }, {signal});
      this.addEventListener('focusout', event => { if (!this.mobile?.open && !this.moving && event.relatedTarget && !this.contains(event.relatedTarget)) this.dismiss(); }, {signal});
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.dismiss(); }, {signal});
      window.addEventListener('resize', () => { if (this.mobile?.open && innerWidth > 749) this.restoreMobile(); this.resize(); }, {signal});
      document.addEventListener('scroll', event => { if(!this.contains(event.target))this.resize(); }, {signal,passive:true,capture:true});
      window.visualViewport?.addEventListener('resize', () => this.resize(), {signal});
      window.visualViewport?.addEventListener('scroll', () => this.resize(), {signal});
      this.closest('dialog')?.addEventListener('close', () => this.dismiss(), {signal});
      if (this.dataset.suggestions === 'true') {
        this.input.setAttribute('role', 'combobox'); this.input.setAttribute('aria-autocomplete', 'list'); this.input.setAttribute('aria-haspopup', 'listbox'); this.input.setAttribute('aria-controls', this.results.id); this.input.setAttribute('aria-expanded', 'false');
        this.results.setAttribute('role', 'listbox'); this.results.setAttribute('aria-label', this.form.getAttribute('aria-label'));
      }
    }
    disconnectedCallback() { this.dismiss(); this.restoreMobile(); this.controller?.abort(); }
    schedule() {
      this.dismiss();
      this.correction=null;
      const query = this.input.value.trim();this.query=query;
      this.closeButton.hidden = !query;
      if(this.clearButton)this.clearButton.hidden = !query;
      if (!query) { this.restoreMobile(true); this.results.replaceChildren(); return; }
      if (this.dataset.suggestions !== 'true') return;
      this.showMobile();
      this.results.replaceChildren(element('p', 'maison-header-search__message', this.dataset.loading));
      this.querySelector('[data-search-all-label]').textContent = this.dataset.allLabel.replace('[query]', query);
      this.open();
      this.timer = setTimeout(() => this.fetchResults(query), 180);
    }
    async fetchResults(query) {
      const sequence = ++this.sequence;
      this.request = new AbortController();
      this.results.setAttribute('aria-busy', 'true'); this.status.textContent = this.dataset.loading;
      const url = new URL(this.dataset.endpoint, location.origin);
      Object.entries({q:query, section_id:'maison-predictive-search', 'resources[type]':'product,collection', 'resources[limit]':this.dataset.limit || '4', 'resources[limit_scope]':'each'}).forEach(([key,value]) => url.searchParams.set(key,value));
      const native = fetch(url, {signal:AbortSignal.any([this.request.signal, AbortSignal.timeout(8000)])}).then(r => { if (!r.ok) throw new Error('Search unavailable'); return r.text(); }).then(parseData);
      const local = catalog(this.dataset.index).then(data => ({products:data.products.filter(p => matchTitle(p.title,query)), collections:data.collections.filter(c => matchTitle(c.title,query))}));
      let data = {products:[], collections:[]}, successes = 0;
      const update = (part, priority) => {
        if (sequence !== this.sequence || !this.isConnected) return;
        successes++;
        for (const kind of ['products','collections']) {
          const list = priority ? [...(part[kind]||[]),...data[kind]] : [...data[kind],...(part[kind]||[])];
          data[kind] = unique(list).sort((a,b)=>(matchTitle(a.title,query)?.score ?? 3)-(matchTitle(b.title,query)?.score ?? 3));
        }
        this.renderResults(data,query);
      };
      await Promise.allSettled([native.then(data => update(data,false)), local.then(data => update(data,true))]);
      if (sequence !== this.sequence || !this.isConnected) return;
      this.results.removeAttribute('aria-busy');
      if (!successes) { this.results.replaceChildren(element('p','maison-header-search__message',this.dataset.unavailable)); this.status.textContent=this.dataset.unavailable; }
    }
    renderResults(data, query) {
      this.results.replaceChildren();
      const matches=[...data.products,...data.collections].map(item=>matchTitle(item.title,query)).filter(Boolean).sort((a,b)=>a.score-b.score);
      this.correction=matches.length&&matches[0].score>0?matches[0].query:null;
      if(this.correction) this.results.append(element('p','tm-search-correction',this.dataset.similarLabel.replace('[query]',this.correction)));
      const addGroup = (kind,label,limit) => {
        const items = data[kind].slice(0,limit); if (!items.length) return;
        const group = element('div','maison-header-search__group'), head = element('div','tm-search-group-head'), title = element('p','maison-header-search__eyebrow',label);
        head.append(title);
        if (kind === 'products') { const all=element('button','tm-search-group-all',this.dataset.viewLabel+' →'); all.type='submit'; all.tabIndex=-1; head.append(all); }
        if (kind === 'collections' && this.dataset.collectionsUrl) { const all=element('a','tm-search-group-all',this.dataset.viewLabel+' →'); all.href=safeURL(this.dataset.collectionsUrl); head.append(all); }
        group.append(head);
        const list=element('div',kind==='products'?'tm-search-pieces':'tm-search-categories');
        if(kind==='products')list.style.setProperty('--suggestion-columns',String(Math.max(2,Math.min(4,items.length))));
        for (const item of items) {
          const link=element('a','maison-header-search__result'+(kind==='collections'?' maison-header-search__result--collection':'')); link.href=safeURL(item.url); link.dataset.maisonSuggestion='';
          if (kind==='collections') {link.append(categoryIcon(item.title),element('span','tm-search-category-title',item.title),element('span','tm-search-category-arrow','›'));}
          else {
            const mat=element('span','tm-search-mat');
            if(item.image && safeURL(item.image)) {const img=element('img','maison-header-search__image');img.src=safeURL(item.image);img.alt='';img.width=360;img.height=360;img.loading='eager';img.decoding='async';mat.append(img);} else mat.append(categoryIcon(''));
            const copy=element('div','maison-header-search__result-copy');copy.append(element('span','tm-search-product-title',item.title));if(item.dimensions)copy.append(element('span','tm-search-dimensions',item.dimensions));copy.append(element('span','maison-header-search__price',item.price || ''));link.append(mat,copy);
          }
          list.append(link);
        }
        group.append(list);this.results.append(group);
      };
      addGroup('products',this.dataset.productsLabel,Number(this.dataset.limit)||4);addGroup('collections',this.dataset.categoriesLabel,this.dataset.compactMobile === 'true' && innerWidth < 750 ? 1 : 2);
      this.options=Array.from(this.results.querySelectorAll('[data-maison-suggestion]'));this.activeIndex=-1;this.input.removeAttribute('aria-activedescendant');
      this.options.forEach((option,i)=>{option.id=this.input.id+'-option-'+i;option.setAttribute('role','option');option.setAttribute('aria-selected','false');option.tabIndex=-1;});
      if(!this.options.length)this.results.append(element('p','maison-header-search__message',this.dataset.emptyLabel));
      this.status.textContent=this.options.length?this.dataset.count.replace('[count]',this.options.length):this.dataset.emptyLabel;
      this.querySelector('[data-search-all-label]').textContent=this.dataset.allLabel.replace('[query]',this.correction||query);
      this.resize();
    }
    showMobile() {
      if(innerWidth>749 || this.mobile?.open || this.closest('dialog[open]'))return;
      this.moving=true;
      if(!this.mobile){this.mobile=element('dialog','tm-search-dialog');this.mobile.setAttribute('aria-label',this.form.getAttribute('aria-label'));this.mobile.addEventListener('cancel',event=>{event.preventDefault();this.exit();});this.append(this.mobile);}
      this.placeholder=element('div');this.placeholder.style.height=this.form.getBoundingClientRect().height+'px';this.form.before(this.placeholder);this.mobile.append(this.form);this.mobile.showModal();this.input.focus({preventScroll:true});this.moving=false;this.resize();
    }
    restoreMobile(keepFocus=false) {
      if(!this.mobile?.open)return;
      this.moving=true;this.input.blur();this.mobile.close();this.placeholder?.replaceWith(this.form);if(keepFocus)this.input.focus({preventScroll:true});this.moving=false;
    }
    resize() {
      const viewport=window.visualViewport;
      const dialog=this.mobile?.open?this.mobile:this.closest('dialog[open]');
      if(dialog && innerWidth<750){dialog.style.setProperty('--search-visible-height',(viewport?.height||innerHeight)+'px');dialog.style.setProperty('--search-visible-top',(viewport?.offsetTop||0)+'px');}
      if(!this.hasAttribute('data-open') || dialog)return;
      this.popup.style.removeProperty('--search-shift');
      const field=this.querySelector('.maison-header-search__field').getBoundingClientRect();
      const header=document.querySelector('#header-component')?.getBoundingClientRect();
      const ceiling=Math.max(16,header?.bottom||0)+12;
      const homeDesktop=innerWidth>=750 && this.closest('.tm-hero-search--desktop');
      if(homeDesktop){
        this.removeAttribute('data-popup-up');this.setAttribute('data-compact-results','');
        const r=this.popup.getBoundingClientRect(), scale=r.width/Math.max(1,this.popup.offsetWidth);
        this.popup.style.setProperty('--search-shift',(Math.min(0,innerWidth-16-r.right)-Math.min(0,r.left-16))/Math.max(.1,scale)+'px');
        if(r.bottom<=ceiling || field.top>=innerHeight)this.dismiss();
        return;
      }
      if(!this.closest('#header-component') && (field.bottom<=ceiling || field.top>=innerHeight)){this.dismiss();return;}
      const below=innerHeight-field.bottom-24, above=field.top-ceiling-12;
      const upward=below<400 && above>below;
      this.toggleAttribute('data-popup-up',upward);
      const scale=this.popup.getBoundingClientRect().width/Math.max(1,this.popup.offsetWidth);
      const panelHeight=Math.max(120,Math.min(660,(upward?above:below)/Math.max(.1,scale)));
      this.toggleAttribute('data-compact-results',panelHeight<520);
      this.popup.style.setProperty('--search-panel-height',panelHeight+'px');
      const r=this.popup.getBoundingClientRect(), available=innerWidth-16;
      this.popup.style.setProperty('--search-shift',(Math.min(0,available-r.right)-Math.min(0,r.left-16))/Math.max(.1,scale)+'px');
    }
    open() { if(!this.input.value.trim())return;this.popup.hidden=false;this.setAttribute('data-open','');this.closeButton.hidden=false;this.input.setAttribute('aria-expanded','true');this.resize(); }
    dismiss() {clearTimeout(this.timer);this.sequence++;this.request?.abort();this.popup&&(this.popup.hidden=true);this.removeAttribute('data-open');this.input?.setAttribute('aria-expanded','false');this.input?.removeAttribute('aria-activedescendant');this.results?.removeAttribute('aria-busy');this.activeIndex=-1;this.options?.forEach(o=>o.setAttribute('aria-selected','false'));}
    clear() {
      this.dismiss();this.correction=null;this.query='';this.input.value='';
      this.results.replaceChildren();this.options=[];this.status.textContent='';
      if(this.clearButton)this.clearButton.hidden=true;
      this.closeButton.hidden=true;this.input.focus({preventScroll:true});
    }
    exit() {
      this.input.blur();this.dismiss();this.restoreMobile();
      const parent=this.closest('dialog-component');if(parent)parent.closeDialog?.();
      this.closeButton.hidden=true;
      if(this.clearButton)this.clearButton.hidden=!this.input.value.trim();
      if(!parent && innerWidth>749)this.input.focus({preventScroll:true});
    }
    keydown(event) {
      if(event.isComposing)return;
      if(event.key==='Escape'){event.preventDefault();event.stopPropagation();this.exit();return;}
      if(event.key==='Enter'&&this.hasAttribute('data-open')&&this.activeIndex>=0){event.preventDefault();this.options[this.activeIndex]?.click();return;}
      if(!['ArrowDown','ArrowUp'].includes(event.key)||!this.hasAttribute('data-open')||!this.options.length)return;
      event.preventDefault();const direction=event.key==='ArrowDown'?1:-1;this.activeIndex=this.activeIndex<0?(direction===1?0:this.options.length-1):(this.activeIndex+direction+this.options.length)%this.options.length;
      this.options.forEach((o,i)=>o.setAttribute('aria-selected',String(i===this.activeIndex)));const option=this.options[this.activeIndex];this.input.setAttribute('aria-activedescendant',option.id);option.scrollIntoView({block:'nearest'});
    }
  }
  customElements.define('maison-header-search',MaisonHeaderSearch);
})();
